import type { ListViewStatus } from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PageResp } from "@/api/page";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { mapWithConcurrency } from "@/utils/concurrency";

// 한 번에 받는 항목 수. 목록이 스크롤 끝에 닿으면 다음 쪽을 이어 받는다.
export const MULTI_DATASET_PAGE_SIZE = 30;

export type MultiDatasetPageQuery = {
  page: number;
  size: number;
  search?: string;
};

export type MultiDatasetPageLoader<T> = (query: MultiDatasetPageQuery) => Promise<PageResp<T>>;

export type MultiDatasetViewControllerOptions<T> = {
  // 한 쪽을 받는 함수. 참조가 바뀌면 다른 목록으로 보고 1쪽부터 다시 받는다. 그래서 호출부는
  // 대상이 같을 때 같은 함수를 넘긴다(`useCallback`).
  loadPage: MultiDatasetPageLoader<T>;
  // 항목의 키. 같은 Dataset 이 목록에 두 번 나올 수 있으므로 Dataset uuid 를 키로 쓰지 않는다.
  // 렌더마다 새 함수를 넘겨도 다시 요청하지 않는다(선택 계산에만 쓴다).
  getKey: (item: T) => string;
  // false 인 동안에는 요청하지 않는다. 보이지 않는 탭·최소화한 창이 쓴다.
  enabled?: boolean;
};

export type MultiDatasetViewController<T> = {
  items: readonly T[];
  // 검색어를 적용한 전체 항목 수.
  total: number;
  status: ListViewStatus;
  errorMessage: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  // 지금 검색어로 이미 받은 쪽들을 갱신한다. 완료 전에는 목록과 선택을 유지한다.
  reload: () => void;
  search: string;
  setSearch: (value: string) => void;
  getKey: (item: T) => string;
  selectedKey: string | null;
  select: (key: string) => void;
};

// 멀티 데이터셋 뷰의 목록 상태. 쪽 누적, 검색어, 선택을 가진다.
//
// 뷰 컴포넌트가 아니라 호스트(창 콘텐츠·탭을 가진 화면)가 이 훅을 부른다. 창을 최소화하거나 탭을
// 바꾸면 뷰는 언마운트된다. 상태를 뷰 안에 두면 돌아올 때마다 목록을 다시 받고 검색어와 선택을 잃는다.
//
// 고른 항목이 없거나 그 항목이 목록에서 빠지면(검색) 첫 항목을 선택으로 읽는다. 그래서 상세 쪽이
// 목록에 없는 항목을 보여주지 않는다. 선택은 저장한 값이 아니라 목록에서 계산한 값이다.
export function useMultiDatasetViewController<T>({
  loadPage,
  getKey,
  enabled = true,
}: MultiDatasetViewControllerOptions<T>): MultiDatasetViewController<T> {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<readonly T[]>([]);
  // 완료된 마지막 쪽이다. reload 의 참조는 쪽 추가와 무관하게 유지한다.
  // 그래야 섬네일 갱신의 최소 간격이 페이지를 읽을 때마다 초기화되지 않는다.
  const pageRef = useRef(0);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<ListViewStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // 사용자가 고른 키. 그 항목이 목록에 없으면 아래에서 첫 항목으로 바꿔 읽는다.
  const [requestedKey, setRequestedKey] = useState<string | null>(null);
  // 늦게 도착한 응답을 버리는 번호. 검색어를 빠르게 바꾸면 앞 요청의 응답이 뒤에 올 수 있다.
  const requestRef = useRef(0);

  const load = useCallback(
    async (mode: "replace" | "append" | "refresh") => {
      const requestId = ++requestRef.current;
      const firstPage = mode === "append" ? pageRef.current + 1 : 1;
      const lastPage = mode === "refresh" ? Math.max(1, pageRef.current) : firstPage;
      setErrorMessage(null);
      if (mode === "replace" || pageRef.current === 0) {
        pageRef.current = 0;
        setStatus("loading");
        setLoadingMore(false);
      } else {
        // 갱신 중에도 행과 상세를 유지한다. 추가 쪽 요청은 완료할 때까지 막는다.
        setStatus((current) => (current === "error" ? "loading" : current));
        setLoadingMore(true);
      }
      try {
        const pages = Array.from(
          { length: lastPage - firstPage + 1 },
          (_, index) => firstPage + index,
        );
        // 이미 읽은 쪽만 다시 받는다. 모든 쪽이 도착한 뒤 교체해야 뒤쪽 선택이 사라지지 않는다.
        const responses = await mapWithConcurrency(
          pages,
          (page) =>
            loadPage({
              page,
              size: MULTI_DATASET_PAGE_SIZE,
              ...(search === "" ? {} : { search }),
            }),
          4,
        );
        if (requestId !== requestRef.current) {
          return;
        }
        setTotal(responses[0].total);
        const received = responses.flatMap((response) => response.items);
        setItems((previous) => (mode === "append" ? [...previous, ...received] : received));
        pageRef.current = lastPage;
        setStatus("success");
      } catch (caught) {
        if (requestId !== requestRef.current) {
          return;
        }
        setStatus("error");
        setErrorMessage(resolveLoadErrorMessage(caught));
      } finally {
        if (requestId === requestRef.current) {
          setLoadingMore(false);
        }
      }
    },
    [loadPage, search],
  );

  // `load` 는 대상(loadPage)이나 검색어가 바뀔 때만 새로 만들어진다. 그래서 이미 받은 `load` 를
  // 기억해 두면, 탭을 떠났다 돌아오는 것(enabled 만 바뀜)으로는 다시 받지 않는다.
  const loadedRef = useRef<typeof load | null>(null);
  useEffect(() => {
    if (!enabled || loadedRef.current === load) {
      return;
    }
    loadedRef.current = load;
    void load("replace");
  }, [enabled, load]);

  // 로딩·오류 상태에서는 감지 요소도 제거된다. 성공할 때 다시 관측하도록 hasMore 를 함께 바꾼다.
  const hasMore = status === "success" && items.length < total;
  const loadMore = useCallback(() => {
    if (enabled && hasMore && !loadingMore) {
      void load("append");
    }
  }, [enabled, hasMore, load, loadingMore]);
  const reload = useCallback(() => void load("refresh"), [load]);

  const selectedKey = useMemo(() => {
    const keys = items.map(getKey);
    return requestedKey !== null && keys.includes(requestedKey) ? requestedKey : (keys[0] ?? null);
  }, [items, getKey, requestedKey]);

  return {
    items,
    total,
    status,
    errorMessage,
    hasMore,
    loadingMore,
    loadMore,
    reload,
    search,
    setSearch,
    getKey,
    selectedKey,
    select: setRequestedKey,
  };
}
